const ISO_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Normalizes ISO date-time strings to millisecond precision.
 *
 * Why needed: request and NOS can represent the same time with different precision,
 * which would otherwise produce false diffs.
 *
 * @example
 * normalizeDateTimePrecision('2026-02-20T15:55:29.052871Z');
 * // => '2026-02-20T15:55:29.052Z'
 */
export function normalizeDateTimePrecision(value: string): string {
  const trimmed = value.trim();
  if (!ISO_DATE_TIME_PATTERN.test(trimmed)) {
    return value;
  }

  const parsed = new Date(trimmed);
  if (!Number.isFinite(parsed.getTime())) {
    return value;
  }

  return parsed.toISOString();
}

/**
 * Converts market payload variants into a sorted unique list of comparable strings.
 *
 * Form outputs Markets as string[]; pre-trade NOS response has Markets as objects
 * (MarketAccount, Market, etc.). Array element order and duplicates must not produce false diffs.
 *
 * @example
 * normalizeMarkets([
 *   { MarketAccount: 'coinbase/alice' },
 *   'b2c2/b2c2',
 *   'coinbase/alice',
 * ]);
 * // => ['b2c2/b2c2', 'coinbase/alice']
 */
export type MarketEntry = string | Record<string, unknown>;

export function normalizeMarkets(value: MarketEntry[]): string[] {
  const normalized = value
    .map((market): string | undefined => {
      if (typeof market === 'string') {
        return market.trim();
      }

      const account = market['MarketAccount'];
      if (typeof account === 'string' && account.trim()) {
        return account.trim();
      }

      const directMarket = market['Market'];
      if (typeof directMarket === 'string' && directMarket.trim()) {
        return directMarket.trim();
      }

      return undefined;
    })
    .filter((market): market is string => typeof market === 'string' && market.length > 0);

  return [...new Set(normalized)].toSorted();
}
